import React, { memo, useState } from 'react';
import { Box, Paper, Typography, IconButton, Chip, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import GanttTimeline from './GanttTimeline';
import { PANEL_CHROME_HEADER_H } from '../layout/appChrome';

const TimelinePanel = memo(function TimelinePanel({
  applications,
  selectedApplication,
  onSelectApplication,
  onSelectProperty,
  dateRange,
  includeAllDates,
  embedded = false,
  mode = 'bottom',
  loading = false,
  showExpandFilters = false,
  onExpandFilters,
  /** When false (tab mode), Gantt stays mounted but idle so scroll position is preserved. */
  isActive = true,
}) {
  const [expanded, setExpanded] = useState(mode === 'tab');
  const [viewMode, setViewMode] = useState('gantt'); // 'gantt' or 'single'
  const [milestonesOnly, setMilestonesOnly] = useState(false);

  // Use the selected application passed from parent
  const selectedApp = selectedApplication;

  const isTabMode = mode === 'tab';
  // Gantt stays mounted when the tab is inactive so scroll position lives in the DOM.
  // (Unmounting remounted at top; Virtuoso then applied initialScrollTop after paint → visible scroll.)

  return (
    <Paper
      elevation={0}
      sx={isTabMode ? {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'hidden',
        backgroundColor: '#ffffff'
      } : embedded ? {
        position: 'relative',
        height: expanded ? '350px' : '48px',
        maxHeight: '50vh',
        backgroundColor: '#f8f6fc',
        borderTop: '2px solid #7454A5',
        transition: 'height 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      } : {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: expanded ? '350px' : '48px',
        maxHeight: '50vh',
        backgroundColor: '#f8f6fc',
        borderTop: '2px solid #7454A5',
        zIndex: 2000,
        transition: 'height 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Bar */}
      {!isTabMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1,
            backgroundColor: '#7454A5',
            color: 'white',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Application Timeline
            </Typography>
            {expanded && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label="All Applications"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('gantt');
                  }}
                  color={viewMode === 'gantt' ? 'primary' : 'default'}
                  sx={{ color: viewMode === 'gantt' ? 'white' : 'inherit' }}
                />
                <Chip
                  label="Single View"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewMode('single');
                  }}
                  color={viewMode === 'single' ? 'primary' : 'default'}
                  sx={{ color: viewMode === 'single' ? 'white' : 'inherit' }}
                />
              </Box>
            )}
          </Box>
          <IconButton
            size="small"
            sx={{ color: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>
      )}

      {/* Timeline Content */}
      {(expanded || isTabMode) && (
        <Box
          sx={{
            flex: 1,
            overflow: isTabMode ? 'hidden' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {isTabMode && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                justifyContent: showExpandFilters ? 'space-between' : 'flex-end',
                gap: 1,
                px: 1.5,
                borderBottom: '1px solid #e5e7eb',
                bgcolor: '#fafafa',
                boxSizing: 'border-box',
                flexShrink: 0,
                minHeight: PANEL_CHROME_HEADER_H,
                height: PANEL_CHROME_HEADER_H,
              }}
            >
              {showExpandFilters && (
                <IconButton
                  size="small"
                  onClick={onExpandFilters}
                  aria-label="Expand filters"
                  sx={{
                    color: '#4b5563',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    p: 0.5,
                    bgcolor: '#fff',
                    flexShrink: 0,
                    '&:hover': { bgcolor: '#f3f4f6' },
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              )}
              <ToggleButtonGroup
                value={milestonesOnly ? 'milestones' : 'all'}
                exclusive
                onChange={(_, val) => { if (val) setMilestonesOnly(val === 'milestones'); }}
                size="small"
                sx={{
                  height: 28,
                  maxWidth: '100%',
                  flexShrink: 1,
                  '& .MuiToggleButtonGroup-grouped': {
                    height: 28,
                  },
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0,
                    lineHeight: 1.2,
                    minHeight: 28,
                    height: 28,
                    color: '#6b7280',
                    borderColor: '#d1d5db',
                    '&.Mui-selected': {
                      bgcolor: '#7454A5',
                      color: '#fff',
                      '&:hover': { bgcolor: '#5e4385' },
                    },
                  },
                }}
              >
                <ToggleButton value="all">All events</ToggleButton>
                <ToggleButton value="milestones">Milestones only</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#7454A5' }} />
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                // Inactive tab: stay mounted (scroll cache) but inert.
                ...(isTabMode && !isActive
                  ? { visibility: 'hidden', pointerEvents: 'none' }
                  : null),
              }}
              aria-hidden={isTabMode && !isActive}
            >
              <GanttTimeline
                applications={applications}
                dateRange={dateRange}
                includeAllDates={includeAllDates}
                onSelectApplication={onSelectApplication}
                onSelectProperty={onSelectProperty}
                selectedApplication={selectedApp}
                milestonesOnly={milestonesOnly}
                isActive={isActive}
              />
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
});

export default TimelinePanel;
